import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Timer, Waves, CloudRain, Wind, Sparkles, Volume2, Layers } from 'lucide-react';
import api from '@/lib/api';
import { ColorNoisePlayer, COLORS, COLOR_META } from '@/lib/colorNoise';
import { SoundscapeMixer } from '@/lib/soundscape';
import { resolveNatureUrl as resolveNatureUrlShared, resolveNatureTrack as resolveNatureTrackShared } from '@/lib/natureTracks';
import { usePlayer } from '@/contexts/PlayerContext';
import { track } from '@/lib/analytics';

// Purpose presets — each maps to a color (or a small choice set) and toggles
// sleep-mode micro-pulses when appropriate.
const PURPOSES = [
    { id: 'memory',      label: 'Memory improvement', primary: 'pink',  options: ['pink'],           sleep: false },
    { id: 'studying',    label: 'Studying',           primary: 'white', options: ['white'],          sleep: false },
    { id: 'focus',       label: 'Focus',              primary: 'pink',  options: ['pink'],           sleep: false },
    { id: 'adhd',        label: 'ADHD support',       primary: 'pink',  options: ['pink', 'white'],  sleep: false },
    { id: 'relax',       label: 'Relaxation',         primary: 'brown', options: ['brown', 'green'], sleep: false },
    { id: 'sleep',       label: 'Sleep (micro-pulses)', primary: 'pink', options: ['pink'],          sleep: true },
    { id: 'custom',      label: 'Just noise',         primary: 'white', options: COLORS,             sleep: false },
];

// Match nature sounds against titles/categories in /api/music.
const NATURE_KEYWORDS = {
    waves:  { label: 'Ocean waves',       tokens: ['ocean', 'waves', 'sea'],                     icon: Waves },
    rain:   { label: 'Rain',              tokens: ['rain'],                                      icon: CloudRain },
    forest: { label: 'Forest breeze',     tokens: ['wind', 'forest', 'trees', 'rainforest'],     icon: Wind },
};

function pickNatureTrack(catalog, kind) { return resolveNatureTrackShared(catalog, kind); }

function fmtCountdown(sec) {
    if (sec == null) return '';
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

export default function ColorSound() {
    const { playTrack, setFullscreen } = usePlayer();
    const [purpose, setPurpose] = useState('focus');
    const [color, setColor] = useState('pink');
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(0.4);
    const [timerMin, setTimerMin] = useState(null);
    const [remaining, setRemaining] = useState(null);
    const [natureCatalog, setNatureCatalog] = useState([]);
    // Optional nature layer blended underneath the colour noise. When set,
    // we switch from the single-source ColorNoisePlayer to a SoundscapeMixer
    // so both can play through one AudioContext with independent levels.
    const [blendNature, setBlendNature] = useState(null); // 'rain'|'waves'|'breeze'|null
    const [blendVolume, setBlendVolume] = useState(0.4);

    const playerRef = useRef(null);
    const mixerRef = useRef(null);
    const timerHandleRef = useRef(null);
    // Tracks the last state we already emitted analytics for, so re-triggers
    // caused by the [color, playing] effect don't double-count in Mongo.
    const lastEmittedRef = useRef(null);

    useEffect(() => {
        api.get('/music').then((r) => setNatureCatalog(r.data || [])).catch(() => {});
        return () => {
            playerRef.current?.stop();
            playerRef.current = null;
            mixerRef.current?.stop();
            mixerRef.current = null;
            if (timerHandleRef.current) clearInterval(timerHandleRef.current);
        };
    }, []);

    // Whenever purpose changes, snap the color to the preset primary.
    useEffect(() => {
        const p = PURPOSES.find((x) => x.id === purpose);
        if (p && !p.options.includes(color)) setColor(p.primary);
    }, [purpose, color]);

    // If the noise is playing and the user changes color/sleep-mode/blend, restart.
    useEffect(() => {
        if (playing) startNoise();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color, blendNature]);

    // Blend volume updates can be applied live without a restart.
    useEffect(() => {
        if (!playing || !mixerRef.current) return;
        // Nature layer is always index 1 when blending.
        mixerRef.current.setLayerVolume(1, blendVolume);
    }, [blendVolume, playing]);

    const currentPurpose = useMemo(() => PURPOSES.find((p) => p.id === purpose), [purpose]);

    const resolveNatureUrl = useMemo(() => (kind) => {
        return resolveNatureUrlShared(natureCatalog, kind);
    }, [natureCatalog]);

    const startNoise = async () => {
        // Stop whichever engine was running — switch cleanly between the
        // single-source player and the mixer when the blend toggles.
        playerRef.current?.stop({ keepContext: true });
        mixerRef.current?.stop({ keepContext: true });

        if (blendNature) {
            if (!mixerRef.current) mixerRef.current = new SoundscapeMixer(resolveNatureUrl);
            mixerRef.current.setMasterBase(1.0);
            mixerRef.current.setDuckRatio(1.0);
            await mixerRef.current.play([
                { kind: 'color', key: color, volume, sleep: !!currentPurpose?.sleep },
                { kind: 'nature', key: blendNature, volume: blendVolume, sleep: false },
            ]);
        } else {
            if (!playerRef.current) playerRef.current = new ColorNoisePlayer();
            playerRef.current.setVolume(volume);
            await playerRef.current.play(color, { sleep: !!currentPurpose?.sleep });
        }
        setPlaying(true);
        const emitKey = `${color}:${purpose}:${blendNature || 'none'}:${timerMin ?? 'inf'}`;
        if (lastEmittedRef.current !== emitKey) {
            lastEmittedRef.current = emitKey;
            track('color-sound-started', { color, purpose, blend: blendNature, timer: timerMin });
        }

        if (timerMin) {
            const endsAt = Date.now() + timerMin * 60_000;
            setRemaining(timerMin * 60);
            if (timerHandleRef.current) clearInterval(timerHandleRef.current);
            timerHandleRef.current = setInterval(() => {
                const rem = Math.round((endsAt - Date.now()) / 1000);
                if (rem <= 0) {
                    clearInterval(timerHandleRef.current);
                    timerHandleRef.current = null;
                    setRemaining(null);
                    stopNoise();
                } else {
                    setRemaining(rem);
                }
            }, 500);
        } else {
            setRemaining(null);
        }
    };

    const stopNoise = () => {
        playerRef.current?.stop({ keepContext: true });
        mixerRef.current?.stop({ keepContext: true });
        setPlaying(false);
        lastEmittedRef.current = null;
        if (timerHandleRef.current) clearInterval(timerHandleRef.current);
        timerHandleRef.current = null;
        setRemaining(null);
    };

    const toggle = () => (playing ? stopNoise() : startNoise());

    const onVolume = (v) => {
        setVolume(v);
        playerRef.current?.setVolume(v);
        // Colour layer is always index 0 inside the mixer.
        mixerRef.current?.setLayerVolume(0, v);
    };

    const playNature = (kind) => {
        const t = resolveNatureTrackShared(natureCatalog, kind);
        if (!t) return;
        // Handing off to the main music player also stops the color-noise
        // synthesis so we're never playing two ambient beds at once.
        stopNoise();
        playTrack({
            id: t.id,
            title: t.title,
            subtitle: t.category,
            image: t.cover_image,
            audio_url: t.audio_url,
            kind: 'music',
            loop: true,
        });
        setFullscreen(true);
        track('nature-sound-started', { kind, track_id: t.id });
    };

    return (
        <div className="space-y-8" data-testid="color-sound-page">
            <header className="space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-forest-soft">Sound bath</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-forest">Colors of sound</h1>
                <p className="text-forest-soft max-w-xl">
                    Continuous, evenly-textured noise for focus, memory, relaxation, and sleep — each colour has a slightly different feel. Pick a purpose or explore the palette.
                </p>
            </header>

            {/* Purpose selector */}
            <section className="qm-card p-6 md:p-8" data-testid="purpose-section">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2">
                            <Sparkles size={18} /> Pick a purpose
                        </h2>
                        <p className="text-sm text-forest-soft">We&rsquo;ll pre-select the colour research most points to.</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {PURPOSES.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setPurpose(p.id)}
                            data-testid={`purpose-${p.id}`}
                            aria-pressed={purpose === p.id}
                            className={`qm-pill text-sm border ${purpose === p.id ? 'bg-forest text-cream border-transparent' : 'bg-white/60 border-forest/20 text-forest hover:bg-sage/30'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                {currentPurpose && currentPurpose.options.length > 1 && (
                    <p className="text-xs text-forest-soft mt-3">
                        Research supports either <strong>{currentPurpose.options.join(' or ')}</strong> for this purpose — feel free to compare.
                    </p>
                )}
            </section>

            {/* Colour selector */}
            <section className="qm-card p-6 md:p-8" data-testid="color-section">
                <h2 className="font-serif text-2xl text-forest mb-4">Choose a colour</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                    {COLORS.map((c) => {
                        const meta = COLOR_META[c];
                        const allowed = currentPurpose ? currentPurpose.options.includes(c) : true;
                        const active = color === c;
                        return (
                            <button
                                key={c}
                                type="button"
                                onClick={() => allowed && setColor(c)}
                                disabled={!allowed}
                                data-testid={`color-${c}`}
                                aria-pressed={active}
                                className={`relative rounded-3xl border p-4 text-left transition ${
                                    active ? 'border-forest ring-2 ring-forest/30' : 'border-transparent hover:border-forest/30'
                                } ${!allowed ? 'opacity-40 cursor-not-allowed' : ''}`}
                                style={{
                                    background: `linear-gradient(160deg, ${meta.hue} 0%, rgba(250,247,242,0.9) 120%)`,
                                }}
                            >
                                <div className="font-serif text-lg text-forest">{meta.label}</div>
                                <p className="text-[11px] text-forest-soft leading-snug mt-1 line-clamp-3">{meta.desc}</p>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Player */}
            <section className="qm-card p-6 md:p-8" data-testid="noise-player">
                <div className="flex flex-wrap items-center gap-5">
                    <button
                        type="button"
                        onClick={toggle}
                        data-testid={playing ? 'noise-pause' : 'noise-play'}
                        aria-label={playing ? 'Stop noise' : 'Play noise'}
                        className="w-16 h-16 rounded-full bg-forest text-cream grid place-items-center hover:bg-forest/90 transition"
                    >
                        {playing ? <Pause size={22} /> : <Play size={22} className="translate-x-0.5" />}
                    </button>

                    <div className="flex-1 min-w-[200px]">
                        <div className="text-xs uppercase tracking-[0.25em] text-forest-soft">Now selected</div>
                        <div className="font-serif text-2xl text-forest" data-testid="noise-selected-label">
                            {COLOR_META[color].label} noise{currentPurpose?.sleep ? ' · sleep pulses' : ''}
                        </div>
                        <div className="text-xs text-forest-soft mt-0.5" data-testid="noise-purpose-label">for {currentPurpose?.label.toLowerCase()}</div>
                    </div>

                    {playing && timerMin != null && (
                        <div className="text-forest font-mono text-lg" data-testid="noise-countdown">
                            <Timer size={14} className="inline mr-1" /> {fmtCountdown(remaining)}
                        </div>
                    )}
                </div>

                {/* Volume */}
                <div className="mt-5 flex items-center gap-3">
                    <Volume2 size={16} className="text-forest-soft" />
                    <input
                        type="range" min="0" max="1" step="0.02"
                        value={volume}
                        onChange={(e) => onVolume(parseFloat(e.target.value))}
                        data-testid="noise-volume"
                        aria-label="Volume"
                        className="w-full accent-forest"
                    />
                </div>

                {/* Timer */}
                <div className="mt-5">
                    <div className="text-xs uppercase tracking-[0.25em] text-forest-soft mb-2">Timer</div>
                    <div className="flex flex-wrap gap-2" data-testid="timer-options">
                        {[null, 15, 30, 45, 60, 90].map((m) => (
                            <button
                                key={m ?? 'inf'}
                                type="button"
                                onClick={() => setTimerMin(m)}
                                data-testid={`timer-${m ?? 'off'}`}
                                aria-pressed={timerMin === m}
                                className={`qm-pill text-sm ${timerMin === m ? 'bg-forest text-cream border-transparent' : 'bg-white/60 border-forest/20 text-forest hover:bg-sage/30'}`}
                            >
                                {m == null ? 'Until I stop' : `${m} min`}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-forest-soft mt-2">Timer takes effect on the next Play.</p>
                </div>
            </section>

            {/* Blend a nature layer */}
            <section className="qm-card p-6 md:p-8" data-testid="blend-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2">
                            <Layers size={18} /> Blend a nature layer
                        </h2>
                        <p className="text-sm text-forest-soft">Keep the colour noise playing — add rain, waves or forest on top at its own level.</p>
                    </div>
                    {blendNature && (
                        <button
                            type="button"
                            onClick={() => setBlendNature(null)}
                            data-testid="blend-clear"
                            className="qm-pill text-sm border border-forest/20 bg-white/60 hover:bg-sage/30"
                        >
                            Remove layer
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="blend-options">
                    {Object.entries(NATURE_KEYWORDS).map(([key, meta]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setBlendNature(blendNature === key ? null : key)}
                            data-testid={`blend-${key}`}
                            aria-pressed={blendNature === key}
                            className={`qm-pill text-sm ${blendNature === key ? 'bg-forest text-cream border-forest' : 'border-forest/20 bg-white/60 hover:bg-sage/30'}`}
                        >
                            {meta.label}
                        </button>
                    ))}
                </div>
                {blendNature && (
                    <div className="mt-4 flex items-center gap-3">
                        <Volume2 size={14} className="text-forest-soft" />
                        <input
                            type="range" min="0" max="1" step="0.02"
                            value={blendVolume}
                            onChange={(e) => setBlendVolume(parseFloat(e.target.value))}
                            data-testid="blend-volume"
                            aria-label="Blend volume"
                            className="w-full accent-forest"
                        />
                        <span className="text-xs text-forest-soft w-10 text-right tabular-nums">{Math.round(blendVolume * 100)}%</span>
                    </div>
                )}
            </section>

            {/* Nature sounds (standalone — replaces the noise entirely) */}
            <section className="qm-card p-6 md:p-8" data-testid="nature-section">
                <h2 className="font-serif text-2xl text-forest mb-1">Or a natural bed</h2>
                <p className="text-sm text-forest-soft mb-4">Real recordings — swap in whenever you want a change from synthesised noise.</p>
                <div className="grid sm:grid-cols-3 gap-3">
                    {Object.entries(NATURE_KEYWORDS).map(([key, meta]) => {
                        const t = resolveNatureTrackShared(natureCatalog, key);
                        const Icon = meta.icon;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => playNature(key)}
                                disabled={!t}
                                data-testid={`nature-${key}`}
                                className="qm-card p-4 text-left flex items-center gap-3 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div
                                    className="w-12 h-12 rounded-2xl bg-sage/40 grid place-items-center shrink-0 overflow-hidden"
                                    style={t?.cover_image ? { backgroundImage: `url(${t.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                                >
                                    {!t?.cover_image && <Icon size={18} className="text-forest" />}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-serif text-base text-forest truncate">{meta.label}</div>
                                    <div className="text-xs text-forest-soft truncate">{t?.title || 'Not available yet'}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
